import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  FiBookOpen,
  FiSearch,
  FiChevronDown,
  FiCheckCircle,
  FiInfo,
  FiSend,
  FiZap,
  FiThumbsUp,
  FiThumbsDown,
} from 'react-icons/fi';
import { Card, Input, Button, Skeleton, PageHeader } from '../components/ui';
import { TzamunAiMark, TzamunAiLogo } from '../components/TzamunAiBrand';

/** Render leftover **bold** as real bold — the model is told plain-text, but belt & braces. */
const renderBold = (text) =>
  text.split(/\*\*(.+?)\*\*/g).map((part, i) =>
    i % 2 === 1 ? (
      <strong key={i} className="font-bold text-accent">
        {part}
      </strong>
    ) : (
      part
    )
  );

/** One-tap answer feedback; feeds the super-admin manual-insights report. */
export function FeedbackButtons({ questionId }) {
  const { t } = useTranslation();
  const [voted, setVoted] = useState(null);
  if (!questionId) return null;
  const vote = async (feedback) => {
    setVoted(feedback); // optimistic — a lost vote is not worth an error toast
    axios.post('/api/manual/feedback', { question_id: questionId, feedback }).catch(() => {});
  };
  if (voted) {
    return <div className="mt-2 text-xs text-content-muted">{t('manual.feedbackThanks')}</div>;
  }
  return (
    <div className="mt-2 flex items-center gap-1 text-content-muted">
      <span className="text-xs me-1">{t('manual.feedbackAsk')}</span>
      <button
        onClick={() => vote('up')}
        className="p-2 rounded-lg hover:bg-emerald-50 hover:text-emerald-600 dark:hover:bg-emerald-500/10"
        aria-label={t('manual.feedbackUp')}
      >
        <FiThumbsUp size={14} />
      </button>
      <button
        onClick={() => vote('down')}
        className="p-2 rounded-lg hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-500/10"
        aria-label={t('manual.feedbackDown')}
      >
        <FiThumbsDown size={14} />
      </button>
    </div>
  );
}

/**
 * GIF/video walkthroughs attached to a manual section — compact thumbnails,
 * tap to play fullscreen. `variant` ('mobile'|'desktop') shows the recording
 * matching the viewer's screen; entries without a variant show everywhere.
 */
export function SectionMedia({ media, lang }) {
  if (!media?.length) return null;
  const variantCls = (v) => (v === 'mobile' ? 'md:hidden' : v === 'desktop' ? 'hidden md:block' : '');
  const openFullscreen = (e) => {
    const v = e.currentTarget.querySelector('video');
    if (!v) return;
    v.controls = true;
    try {
      // iOS Safari only fullscreens the <video> element itself
      (v.requestFullscreen || v.webkitRequestFullscreen || v.webkitEnterFullscreen)?.call(v);
    } catch (_) {
      /* fullscreen blocked → inline playback below still works */
    }
    v.play().catch(() => {});
  };
  return (
    <div className="flex flex-wrap gap-3">
      {media.map((m, i) => (
        <figure key={i} className={variantCls(m.variant)}>
          {m.type === 'video' ? (
            <div
              role="button"
              tabIndex={0}
              onClick={openFullscreen}
              onKeyDown={(e) => e.key === 'Enter' && openFullscreen(e)}
              className="relative w-44 md:w-56 rounded-xl overflow-hidden border border-edge cursor-pointer group"
            >
              {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
              <video src={`${m.src}#t=0.5`} preload="metadata" muted playsInline className="w-full pointer-events-none" />
              <div className="absolute inset-0 flex items-center justify-center bg-black/25 group-hover:bg-black/35 transition-colors">
                <span className="w-11 h-11 rounded-full bg-white/90 text-slate-900 flex items-center justify-center shadow-lg">
                  <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true">
                    <path d="M8 5.5v13l11-6.5z" />
                  </svg>
                </span>
              </div>
            </div>
          ) : (
            <img
              src={m.src}
              alt={m.caption?.[lang] || ''}
              className="w-44 md:w-56 rounded-xl border border-edge"
              loading="lazy"
            />
          )}
          {m.caption && (
            <figcaption className="text-xs text-content-muted mt-1 w-44 md:w-56">{m.caption[lang]}</figcaption>
          )}
        </figure>
      ))}
    </div>
  );
}

/**
 * Colorful answer body: numbered lines become accent step rows (same look as
 * the manual sections), tip lines become amber callouts, the rest paragraphs.
 */
export function AnswerBody({ text }) {
  const lines = String(text || '')
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean);
  return (
    <div className="space-y-2">
      {lines.map((line, i) => {
        const step = line.match(/^(\d+)[.)-]\s*(.+)$/);
        if (step) {
          return (
            <div key={i} className="flex gap-3 text-sm text-content leading-7">
              <span className="shrink-0 w-6 h-6 rounded-full bg-accent text-white text-xs font-bold flex items-center justify-center mt-1">
                {step[1]}
              </span>
              <span>{renderBold(step[2])}</span>
            </div>
          );
        }
        const tip = line.match(/^(?:Tip|نصيحة|ملاحظة)\s*[::]\s*(.+)$/i);
        if (tip) {
          return (
            <div
              key={i}
              className="flex gap-2 text-xs rounded-lg bg-amber-50 dark:bg-amber-500/10 text-amber-800 dark:text-amber-200 p-3 leading-6"
            >
              <FiInfo className="shrink-0 mt-0.5" />
              <span>{renderBold(tip[1])}</span>
            </div>
          );
        }
        return (
          <p key={i} className="text-sm text-content-secondary leading-7">
            {renderBold(line.replace(/^[-•]\s*/, ''))}
          </p>
        );
      })}
    </div>
  );
}

/**
 * In-app user manual (AR/EN) + "Ask AI" grounded on the manual content.
 * Content comes from GET /api/manual (single source shared with the AI).
 */
export default function UserManual() {
  const { t, i18n } = useTranslation();
  const isAr = (i18n.language || 'en').startsWith('ar');
  const L = isAr ? 'ar' : 'en';

  const [sections, setSections] = useState(null);
  // Seeded from the command palette: searching there and finding nothing offers
  // "search the manual for X", which lands here with the term already applied
  // rather than making the user retype it.
  const location = useLocation();
  const navigate = useNavigate();
  const [query, setQuery] = useState(() => location.state?.q || '');
  const [openId, setOpenId] = useState(null);

  // Clear the router state once consumed, so a refresh or a back-navigation does
  // not silently re-apply a search the user has since cleared.
  useEffect(() => {
    if (location.state?.q) navigate(location.pathname, { replace: true, state: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [question, setQuestion] = useState('');
  const [asking, setAsking] = useState(false);
  const [answer, setAnswer] = useState(null);
  const sectionRefs = useRef({});

  useEffect(() => {
    axios
      .get('/api/manual')
      .then(({ data }) => setSections(data.data))
      .catch(() => toast.error(t('manual.loadFailed')));
  }, [t]);

  const filtered = useMemo(() => {
    if (!sections) return [];
    const q = query.trim().toLowerCase();
    if (!q) return sections;
    return sections.filter((s) => {
      const hay = [
        s.title.en,
        s.title.ar,
        s.intro.en,
        s.intro.ar,
        (s.keywords || []).join(' '),
        ...s.steps.map((st) => `${st.en} ${st.ar}`),
      ]
        .join(' ')
        .toLowerCase();
      return hay.includes(q);
    });
  }, [sections, query]);

  const scrollTo = (id) => {
    setOpenId(id);
    setTimeout(() => sectionRefs.current[id]?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
  };

  const ask = async (text) => {
    const q = (typeof text === 'string' ? text : question).trim();
    if (!q) return;
    setQuestion(q);
    setAsking(true);
    setAnswer(null);
    try {
      const { data } = await axios.post('/api/manual/ask', { question: q });
      setAnswer(data.data);
    } catch (e) {
      if (e.response?.data?.code === 'ai_quota_exceeded') {
        toast.error(t('manual.quotaError'));
      } else {
        toast.error(t('manual.askFailed'));
      }
    } finally {
      setAsking(false);
    }
  };

  return (
    <div className="space-y-6 pb-10">
      <PageHeader title={t('manual.title')} subtitle={t('manual.subtitle')} icon={FiBookOpen} />

      {/* Ask AI */}
      <Card className="p-5 border-2 border-accent/30">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <TzamunAiMark variant="color" className="h-6" />
            <h2 className="font-bold text-content">{t('manual.askTitle')}</h2>
          </div>
          <div className="flex items-center gap-1.5 text-[10px] text-content-muted">
            <span>{t('manual.poweredBy')}</span>
            <TzamunAiLogo className="h-3.5" />
          </div>
        </div>
        <p className="text-sm text-content-secondary mb-3">{t('manual.askSubtitle')}</p>
        <div className="flex gap-2">
          <Input
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && !asking && ask()}
            placeholder={t('manual.askPlaceholder')}
            maxLength={500}
            className="flex-1"
          />
          <Button onClick={() => ask()} loading={asking} disabled={!question.trim()}>
            <FiSend className="me-1" /> {t('manual.askButton')}
          </Button>
        </div>
        {sections && !answer && !asking && (
          <div className="mt-3 flex flex-wrap gap-2">
            {['job-card-create', 'quotations', 'invoices', 'payments']
              .map((id) => sections.find((s) => s.id === id)?.suggested?.[0])
              .filter(Boolean)
              .map((sq) => (
                <button
                  key={sq.en}
                  onClick={() => ask(sq[L])}
                  className="px-3 py-1.5 rounded-full text-xs bg-accent/5 border border-accent/20 text-accent hover:bg-accent/15"
                >
                  {sq[L]}
                </button>
              ))}
          </div>
        )}
        {answer && (
          <div className="mt-4 rounded-xl bg-surface-secondary p-4 border border-edge">
            <AnswerBody text={answer.answer} />
            <FeedbackButtons questionId={answer.question_id} />
            {answer.sections?.length > 0 && (
              <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-content-muted">
                <span>{t('manual.answerFrom')}:</span>
                {answer.sections.map((s) =>
                  s.internal ? (
                    <span key={s.id} className="px-2 py-1 rounded-full bg-surface-2 text-content-muted">
                      {s.title[L]}
                    </span>
                  ) : (
                    <button
                      key={s.id}
                      onClick={() => scrollTo(s.id)}
                      className="px-2 py-1 rounded-full bg-accent/10 text-accent hover:bg-accent/20"
                    >
                      {s.title[L]}
                    </button>
                  )
                )}
              </div>
            )}
          </div>
        )}
      </Card>

      {/* Search */}
      <div className="relative">
        <FiSearch className="absolute top-3 start-3 text-content-muted" />
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t('manual.searchPlaceholder')}
          className="ps-9"
        />
      </div>

      {/* TOC chips */}
      {sections && !query && (
        <div className="flex flex-wrap gap-2">
          {sections.map((s) => (
            <button
              key={s.id}
              onClick={() => scrollTo(s.id)}
              className="px-3 py-1.5 rounded-full text-xs border border-edge text-content-secondary hover:border-accent hover:text-accent"
            >
              {s.title[L]}
            </button>
          ))}
        </div>
      )}

      {/* Sections */}
      {!sections ? (
        <Skeleton className="h-64" />
      ) : (
        <div className="space-y-3">
          {filtered.map((s) => {
            const open = openId === s.id || !!query;
            return (
              <div
                key={s.id}
                ref={(el) => {
                  sectionRefs.current[s.id] = el;
                }}
              >
              <Card className="overflow-hidden">
                <button
                  onClick={() => setOpenId(open && !query ? null : s.id)}
                  className="w-full flex items-center justify-between p-4 text-start"
                >
                  <span className="font-bold text-content">{s.title[L]}</span>
                  <FiChevronDown
                    className={`text-content-muted transition-transform ${open ? 'rotate-180' : ''}`}
                  />
                </button>
                {open && (
                  <div className="px-4 pb-4 space-y-3">
                    <p className="text-sm text-content-secondary leading-7">{s.intro[L]}</p>
                    <ol className="space-y-2">
                      {s.steps.map((st, i) => (
                        <li key={i} className="flex gap-3 text-sm text-content leading-7">
                          <span className="shrink-0 w-6 h-6 rounded-full bg-accent/10 text-accent text-xs font-bold flex items-center justify-center mt-1">
                            {i + 1}
                          </span>
                          <span>{st[L]}</span>
                        </li>
                      ))}
                    </ol>
                    {s.tips.map((tip, i) => (
                      <div
                        key={i}
                        className="flex gap-2 text-xs rounded-lg bg-amber-50 dark:bg-amber-500/10 text-amber-800 dark:text-amber-200 p-3 leading-6"
                      >
                        <FiInfo className="shrink-0 mt-0.5" />
                        <span>{tip[L]}</span>
                      </div>
                    ))}
                    <SectionMedia media={s.media} lang={L} />
                  </div>
                )}
              </Card>
              </div>
            );
          })}
          {filtered.length === 0 && (
            <div className="text-center text-content-muted py-10 text-sm">
              <FiCheckCircle className="mx-auto mb-2" size={20} />
              {t('manual.noResults')}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
